import { useParams } from "react-router-dom";
import { useGetNotificacionesQuery } from "../services/docs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/Acordion";
import { Spinner } from "../components/Spinner";

export const AcordionNotificacionesMailComponent = () => {
  const { siniestro = "0", tramite_id = "0" } = useParams();

  const { data, isLoading } = useGetNotificacionesQuery({
    siniestro,
    tramite: tramite_id,
  });

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  const correos = data?.notificacionesAppMail || [];

  // Función para formatear la fecha de manera más limpia y segura
  const formatFechaDate = (dateString: string) => {
    if (!dateString) return "Fecha no disponible";
    const date = new Date(dateString);
    return date.toLocaleString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="activos"
      className="w-full pt-3 rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <AccordionItem value="activos" className="border-b-0">

        {/* --- HEADER DEL ACORDEÓN --- */}
        <AccordionTrigger className="flex w-full items-center justify-between px-5 py-4 hover:bg-slate-50 hover:no-underline data-[state=open]:border-b data-[state=open]:border-slate-100">
          <div className="flex items-center gap-2">
            <div className="h-4 w-1.5 rounded bg-sky-500"></div>
            <h3 className="flex items-center gap-2 text-sm font-bold text-slate-800">
              <svg className="h-4 w-4 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
              Historial de Correos Electrónicos
            </h3>
            <span className="font-mono text-xs font-normal text-slate-400">
              ({correos.length} enviados)
            </span>
          </div>
        </AccordionTrigger>

        {/* --- CONTENIDO DESPLEGABLE --- */}
        <AccordionContent className="px-5 py-6 text-sm">
          {correos.length === 0 ? (

            /* Empty State */
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/50 py-10 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 text-sky-400">
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
              </div>
              <p className="text-sm font-bold text-slate-600">No hay correos registrados</p>
              <p className="mt-1 text-xs text-slate-400">Aún no se han enviado notificaciones por correo a este asegurado.</p>
            </div>

          ) : (

            /* Lista de Correos */
            <div className="flex flex-col gap-4">
              {correos.map((e: any) => {
                const isSent = e.fi_estatus_envio === 1;

                return (
                  <article
                    key={e.id}
                    className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300 sm:flex-row sm:items-center sm:justify-between"
                  >
                    {/* Info del Correo */}
                    <div className="flex items-start gap-4">
                      <div className={`mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isSent ? "bg-emerald-50 text-emerald-500" : "bg-rose-50 text-rose-500"}`}>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path></svg>
                      </div>

                      <div className="flex flex-col">
                        <h4 className="text-sm font-bold text-slate-800">
                          {e.fc_notificacion}
                        </h4>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1 font-medium text-slate-700">
                            <svg className="h-3.5 w-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                            {e.fc_correo}
                          </span>
                          <span className="hidden text-slate-300 sm:inline">•</span>
                          <span className="flex items-center gap-1 font-mono">
                            <svg className="h-3.5 w-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                            {formatFechaDate(e.fd_fecha_envio)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Estatus y Acción */}
                    <div className="flex items-center justify-between gap-4 border-t border-slate-100 pt-3 sm:border-0 sm:pt-0">
                      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold ${isSent
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-rose-200 bg-rose-50 text-rose-700"
                        }`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${isSent ? "bg-emerald-500" : "bg-rose-500"}`}></span>
                        {isSent ? "Mensaje Enviado" : "Error de Envío"}
                      </span>

                      {e.fc_mail && (
                        <a
                          href={e.fc_mail}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-brand-600 shadow-sm transition hover:bg-slate-50 hover:text-brand-700"
                        >
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                          Ver Correo
                        </a>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>

          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
